import { notify } from '@billy/design-system'
import { trackError } from '@billy/track-error'
import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { NotificationKeys } from '../../../../../enums/notificationKeys'
import { useValidateReviewEntry } from '../../../hooks/useValidateReviewEntry'
import type { ReviewEntryDataFormValues } from '../../../types/getReviewEntryDataFormSchema'
import type { ReviewEntryTableRow } from '../../../types/reviewEntryTableRow'
import { buildValidateReviewEntryPayload } from '../../../utils/buildValidateReviewEntryPayload'
import { removeReviewEntryDraft } from '../../../utils/reviewEntryDraftStorage'

interface UseSubmitReviewEntryProps {
  documentId?: string
  entry: ReviewEntryTableRow
  isLastEntry: boolean
  journalCode?: string
  onClose: () => void
  onNavigateNext?: () => void
  sourceFingerprint?: string
}

interface UseSubmitReviewEntryResult {
  isProcessing: boolean
  onFormReadyChange: (isReady: boolean) => void
  onSubmit: (values: ReviewEntryDataFormValues) => void
}

export const useSubmitReviewEntry = ({
  documentId,
  entry,
  isLastEntry,
  journalCode,
  onClose,
  onNavigateNext,
  sourceFingerprint,
}: UseSubmitReviewEntryProps): UseSubmitReviewEntryResult => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()

  const { isProcessing, validate } = useValidateReviewEntry({
    onError: (error) => {
      trackError(error)
      notify({
        id: NotificationKeys.ReviewEntryValidate,
        message: t('review_entries.detail.validate_error'),
        variant: 'error',
      })
    },
  })

  // async defaults (purchase VAT rates + supplier prefill) resolve inside `ReviewEntryDataForm`
  // via a one-shot `reset`. we intentionally keep the Validate button clickable so a click on
  // an untouched form still triggers the Zod schema and surfaces "missing field" errors —
  // that's helpful feedback. but if the schema happens to pass on the pre-reset snapshot
  // (e.g. stale taxRateId not yet matched, supplierId not yet resolved), we must not fire the
  // API mutation with that stale data (ADVP-267 guard).
  const [isFormReady, setIsFormReady] = useState(false)

  // `ReviewEntryDataForm` remounts on `key={entry.entryId}` so its own ready state resets to
  // false on entry switch, but this parent-side flag would otherwise stay true from the previous
  // entry — or from the previous API fingerprint on the same entry — until the child fires
  // `onFormReadyChange(true)` again. reset synchronously during render (documented React pattern)
  // so the submit guard is never stale between entries or source-data refreshes.
  const lastDefinedFingerprintRef = useRef(sourceFingerprint)
  const lastEntryIdRef = useRef(entry.entryId)

  if (lastEntryIdRef.current !== entry.entryId) {
    lastEntryIdRef.current = entry.entryId
    lastDefinedFingerprintRef.current = sourceFingerprint
  } else if (sourceFingerprint !== undefined) {
    lastDefinedFingerprintRef.current = sourceFingerprint
  }

  const formSourceKey = `${entry.entryId}::${sourceFingerprint ?? lastDefinedFingerprintRef.current ?? ''}`
  const [previousFormSourceKey, setPreviousFormSourceKey] = useState(formSourceKey)

  if (previousFormSourceKey !== formSourceKey) {
    setPreviousFormSourceKey(formSourceKey)
    setIsFormReady(false)
  }

  const onSubmit = useCallback(
    (values: ReviewEntryDataFormValues) => {
      const organizationId = organization?.id

      if (!organizationId || !documentId || !isFormReady) {
        trackError(
          new Error(
            `Review entry validation skipped (${[
              !organizationId && 'missing organizationId',
              !documentId && 'missing documentId',
              !isFormReady && 'form not ready',
            ]
              .filter(Boolean)
              .join(', ')})`,
          ),
        )
        return
      }

      validate(
        {
          documentId,
          organizationId,
          payload: buildValidateReviewEntryPayload({
            formValues: values,
            journalCode,
          }),
        },
        {
          onSuccess: () => {
            removeReviewEntryDraft({ entryId: entry.entryId, organizationId })
            notify({
              id: NotificationKeys.ReviewEntryValidate,
              message: t('review_entries.detail.validate_success', { invoiceNumber: values.invoiceNumber ?? '' }),
              variant: 'success',
            })

            if (isLastEntry) {
              onClose()
            } else {
              onNavigateNext?.()
            }
          },
        },
      )
    },
    [
      documentId,
      entry.entryId,
      isFormReady,
      isLastEntry,
      journalCode,
      onClose,
      onNavigateNext,
      organization?.id,
      t,
      validate,
    ],
  )

  return { isProcessing, onFormReadyChange: setIsFormReady, onSubmit }
}
